if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {

    connectedCallback() {
      this._viewer     = this.querySelector('[id^="GalleryViewer"]');
      this._thumbsEl   = this.querySelector('[id^="GalleryThumbnails"]');
      this._liveRegion = this.querySelector('[id^="GalleryStatus"]');
      this._layout     = this.dataset.desktopLayout || '';

      if (!this._thumbsEl || !this._viewer) return;

      // ── Thumbnail clicks via event delegation ──────────────────────────────
      // Works regardless of nesting depth or dynamic prepend/reorder.
      this._thumbsEl.addEventListener('click', (e) => {
        const item = e.target.closest('[data-target]');
        if (!item) return;
        e.preventDefault();
        this.setActiveMedia(item.dataset.target, false, true);
      });

      // ── Sync thumbnail highlight when user swipes on mobile ────────────────
      this._viewer.addEventListener('slideChanged', (e) => {
        if (!e.detail || !e.detail.currentElement) return;
        const id = e.detail.currentElement.dataset.mediaId;
        if (id) this._highlightThumbnail(id);
      });

      // ── Desktop: hide non-active slides in thumbnail_slider layout ─────────
      if (this._layout === 'thumbnail_slider' && window.matchMedia('(min-width:750px)').matches) {
        const slider = this._viewer.querySelector('[id^="Slider-"]');
        if (slider) {
          slider.setAttribute('role', 'presentation');
          this._viewer.querySelectorAll('[id^="Slide-"]').forEach(s => s.setAttribute('role', 'presentation'));
        }
      }
    }

    // Public – also called by variant selectors when switching variants
    setActiveMedia(mediaId, prepend, scrollIntoView) {
      const slide = this._viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;

      // 1. Toggle is-active on all main slides
      this._viewer.querySelectorAll('[data-media-id]').forEach(el => el.classList.remove('is-active'));
      slide.classList.add('is-active');

      // 2. Optionally prepend to front (variant switch)
      if (prepend) {
        const slideList = slide.parentElement;
        if (slideList && slideList.firstChild !== slide) {
          slideList.prepend(slide);
        }
        if (this._thumbsEl) {
          const thumb = this._thumbsEl.querySelector(`[data-target="${mediaId}"]`);
          if (thumb && thumb.parentElement.firstChild !== thumb) {
            thumb.parentElement.prepend(thumb);
          }
        }
        // Reset slider page tracking after reorder
        if (this._viewer.slider && typeof this._viewer.resetPages === 'function') {
          this._viewer.resetPages();
        }
      }

      // 3. Scroll main gallery to this slide
      requestAnimationFrame(() => {
        const list = slide.parentElement;
        if (list && typeof list.scrollTo === 'function') {
          const isRTL = false;
          if (prepend) {
            // Scroll to the visual start: 0 in LTR, max scroll in RTL
            list.scrollTo({ left: isRTL ? (list.scrollWidth - list.clientWidth) : 0, behavior: 'smooth' });
          } else {
            // thumbnail_slider: CSS shows only .is-active, scrollTo syncs underlying slider
            // mobile slider: scroll to the slide's position
            const targetLeft = isRTL
              ? (list.scrollWidth - list.clientWidth - slide.offsetLeft + (list.offsetLeft || 0))
              : slide.offsetLeft;
            list.scrollTo({ left: targetLeft, behavior: 'smooth' });
          }
        }
        // Stacked layout: scroll the slide into the visible viewport area
        if (scrollIntoView && this._layout === 'stacked') {
          slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });

      // 4. Autoplay deferred media (video / 3D model)
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferred = slide.querySelector('.deferred-media');
      if (deferred && typeof deferred.loadContent === 'function') deferred.loadContent(false);

      // 5. Prevent sticky header jumping into view
      const sticky = document.querySelector('sticky-header');
      if (sticky) sticky.dispatchEvent(new Event('preventHeaderReveal'));

      // 6. Highlight the matching thumbnail
      this._highlightThumbnail(mediaId);

      // 7. Announce to screen readers
      if (scrollIntoView !== false) {
        const thumb = this._thumbsEl.querySelector(`[data-target="${mediaId}"]`);
        if (thumb) this._announce(slide, thumb.dataset.mediaPosition);
      }
    }

    _highlightThumbnail(mediaId) {
      if (!this._thumbsEl) return;

      // Remove active state from every thumbnail item
      this._thumbsEl.querySelectorAll('[data-target]').forEach(item => {
        item.classList.remove('is-active');
        const btn = item.querySelector('button');
        if (btn) btn.removeAttribute('aria-current');
      });

      // Add active state to the matching thumbnail
      const active = this._thumbsEl.querySelector(`[data-target="${mediaId}"]`);
      if (!active) return;
      active.classList.add('is-active');
      const btn = active.querySelector('button');
      if (btn) btn.setAttribute('aria-current', 'true');

      // Scroll thumbnail strip so the active thumb is visible
      const strip = this._thumbsEl.querySelector('[id^="Slider-"]') || this._thumbsEl.slider;
      if (strip && typeof strip.scrollTo === 'function') {
        const isRTL = false;
        const targetLeft = isRTL
          ? (strip.scrollWidth - strip.clientWidth - active.offsetLeft + (strip.offsetLeft || 0))
          : active.offsetLeft;
        strip.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }
    }

    _announce(slide, position) {
      const img = slide.querySelector('.product__modal-opener--image img');
      const lr  = this._liveRegion;
      if (!img || !lr) return;
      const strings = window.theme && theme.accessibilityStrings;
      if (!strings || !strings.imageAvailable) return;
      img.onload = () => {
        lr.setAttribute('aria-hidden', 'false');
        lr.innerHTML = strings.imageAvailable.replace('[index]', position);
        setTimeout(() => lr.setAttribute('aria-hidden', 'true'), 2000);
      };
      img.src = img.src; // trigger onload if already cached
    }
  });
}